/**
 * Browser side of the SportsFuture Netlify functions. Holds no secrets: Pinata and
 * Telegram credentials live only in the functions (see src/server/*).
 */

import type { ApplicationEnvelope } from './applicationEnvelope';
import { ipfsUri } from './ipfs';
import {
  MAX_APPLICATION_DOCUMENT_BYTES,
  MAX_APPLICATION_JSON_BYTES,
  PIN_FUNCTION_PATH,
  SESSION_HEADER,
  TELEGRAM_FUNCTION_PATH,
  encodeSessionProof,
  type ApplicationRole,
  type ContactNotifyPayload,
  type SessionProof,
  type TelegramNotifyPayload,
} from './serverContract';
import { readStoredClaim, type Web3Claim } from './siweSession';

/** Re-sign first if the stored sign-in has less than this left (the server allows 15 min). */
export const SESSION_MIN_REMAINING_MS = 3 * 60 * 1000;

export type PinJsonFn = (
  envelope: ApplicationEnvelope,
  name: string
) => Promise<{ cid: string; uri: string }>;

function mb(n: number): string {
  return `${Math.round(n / (1024 * 1024))} MB`;
}

/**
 * The wallet sign-in proof for the server functions. Reuses the stored Sign-in claim;
 * only asks the wallet to sign again when it is missing or about to expire.
 */
export async function ensureSessionProof(
  signIn: () => Promise<void>,
  opts?: { now?: () => number; read?: () => Web3Claim | null }
): Promise<SessionProof> {
  const now = opts?.now ?? Date.now;
  const read = opts?.read ?? (() => readStoredClaim());
  let claim = read();
  if (!claim || claim.eligibleUntil - now() < SESSION_MIN_REMAINING_MS) {
    await signIn();
    claim = read();
  }
  if (!claim) throw new Error('Sign in with your wallet to submit.');
  return { message: claim.message, signature: claim.signature };
}

async function errorText(res: Response, fallback: string): Promise<string> {
  try {
    const data = (await res.json()) as { error?: unknown };
    if (typeof data?.error === 'string' && data.error.trim()) return data.error;
  } catch {
    /* not JSON */
  }
  return `${fallback} (${res.status})`;
}

export async function pinApplicationEnvelope(
  envelope: ApplicationEnvelope,
  opts: { session: SessionProof; fetchImpl?: typeof fetch }
): Promise<{ cid: string; uri: string }> {
  const body = JSON.stringify(envelope);
  if (new TextEncoder().encode(body).length > MAX_APPLICATION_JSON_BYTES) {
    throw new Error(
      `Your application is too large to upload (max ${mb(MAX_APPLICATION_JSON_BYTES)}). ` +
        `Attach a smaller document (max ${mb(MAX_APPLICATION_DOCUMENT_BYTES)}).`
    );
  }
  const fetchImpl = opts.fetchImpl ?? fetch;
  const res = await fetchImpl(PIN_FUNCTION_PATH, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      [SESSION_HEADER]: encodeSessionProof(opts.session),
    },
    body,
  });
  if (!res.ok) throw new Error(await errorText(res, 'Could not upload the application to IPFS'));
  const data = (await res.json()) as { cid?: unknown };
  if (typeof data.cid !== 'string' || !data.cid) throw new Error('IPFS upload returned no CID.');
  return { cid: data.cid, uri: ipfsUri(data.cid) };
}

/** Default pinJson for persistAndSubmitApplication. */
export function serverPinJson(session: SessionProof, fetchImpl?: typeof fetch): PinJsonFn {
  return (envelope) => pinApplicationEnvelope(envelope, { session, fetchImpl });
}

/**
 * Ask the server to post a Telegram notification. Resolves { sent: false } when the server
 * has no Telegram settings (503), like the old "configuration missing" branch; throws on
 * any other failure.
 */
export async function sendTelegramNotify(
  payload: TelegramNotifyPayload,
  opts?: { session?: SessionProof; fetchImpl?: typeof fetch }
): Promise<{ sent: boolean }> {
  const fetchImpl = opts?.fetchImpl ?? fetch;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (opts?.session) headers[SESSION_HEADER] = encodeSessionProof(opts.session);
  const res = await fetchImpl(TELEGRAM_FUNCTION_PATH, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  if (res.status === 503) return { sent: false };
  if (!res.ok) throw new Error(await errorText(res, 'Failed to send notification'));
  return { sent: true };
}

export function notifyApplicationSubmitted(opts: {
  role: ApplicationRole;
  didId: number;
  session: SessionProof;
  fetchImpl?: typeof fetch;
}): Promise<{ sent: boolean }> {
  return sendTelegramNotify(
    { kind: 'application', role: opts.role, didId: opts.didId },
    { session: opts.session, fetchImpl: opts.fetchImpl }
  );
}

export function notifyContact(
  payload: Omit<ContactNotifyPayload, 'kind'>,
  opts?: { fetchImpl?: typeof fetch }
): Promise<{ sent: boolean }> {
  return sendTelegramNotify({ kind: 'contact', ...payload }, opts);
}
